import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

/**
 * Shared policy page. Each section's `body` can be either a single string or
 * an array of paragraphs. Nested arrays render as bullet lists.
 */
const policies = {
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'August 2026',
    intro:
      'This policy explains how Science Sorted collects, uses and protects your personal information when you use our website or tuition services.',
    sections: [
      {
        h: '1. Who we are',
        body: [
          'Science Sorted Ltd (“Science Sorted”, “we”, “us” or “our”) provides online GCSE and A Level science tuition to students across the UK.',
          'For the purposes of UK data protection law, Science Sorted Ltd is the data controller for the personal information described in this policy.',
          'Contact: info@sciencesorted.co.uk',
          'Company number: 16479246',
        ],
      },
      {
        h: '2. The information we collect',
        body: [
          'We may collect personal information about parents, guardians and students when you enquire about our services, book a trial lesson, enrol on a course, communicate with us or use our website.',
          'This may include names, email addresses, telephone numbers, student year group, subjects studied, exam board, tuition preferences, attendance information, assessment results, homework and progress information, communications with us and billing or administrative information.',
          'We may also collect technical information relating to use of our website, such as browser type, device information, IP address and cookie preferences.',
          'Where information about a student is required to provide tuition, we aim to collect only what is reasonably necessary.',
        ],
      },
      {
        h: '3. How we use personal information',
        body: [
          'We may use personal information to respond to enquiries, arrange free trial lessons, enrol students, deliver tuition, provide learning resources, monitor attendance and progress, communicate with parents and students, administer payments and invoices, provide support, maintain safeguarding records, improve our services and meet our legal and regulatory obligations.',
          'Our lawful bases may include performance of a contract, taking steps before entering into a contract, compliance with legal obligations and our legitimate interests in operating and improving Science Sorted.',
          'We will use consent where consent is the appropriate lawful basis, for example for certain marketing or optional website technologies.',
          'The ICO requires organisations to identify a lawful basis before processing personal information, and additional care is required when handling children\u2019s information.',
        ],
      },
      {
        h: '4. Children\u2019s personal information',
        body: [
          'Many Science Sorted students are under 18.',
          'We recognise that children\u2019s personal information requires particular protection and aim to use it fairly, transparently and in the student\u2019s best interests. Parents or guardians will normally be the primary contractual contact for students under 18.',
          'We will not use children\u2019s information for behavioural advertising or unrelated commercial profiling.',
          'Where privacy information is directed at students, we aim to explain it using language appropriate to their age. ICO guidance specifically requires organisations providing online services likely to be accessed by children to consider their needs and provide clear, age-appropriate information.',
        ],
      },
      {
        h: '5. Special category information',
        body: [
          'We do not routinely request sensitive personal information.',
          'Occasionally, a parent may need to tell us about health, accessibility or other circumstances relevant to safely or effectively providing tuition. Where such information is necessary, we will restrict its use and access and process it only where we have an appropriate legal basis.',
        ],
      },
      {
        h: '6. Who we may share information with',
        body: [
          'We use trusted service providers to help operate Science Sorted.',
          'Depending on the services being used, these may include providers of online teaching and communication platforms, student administration systems, email services, website hosting, databases, accounting systems and cloud productivity tools.',
          'This may include services such as Google Workspace, Google Meet, Google Classroom, TutorBird and Resend.',
          'We only provide service providers with information reasonably necessary for the relevant service and expect appropriate data-protection and security measures to be used.',
          'We may also disclose information where required by law, to protect a child or other person from harm, or to establish, exercise or defend legal rights.',
        ],
      },
      {
        h: '7. International transfers',
        body: [
          'Some technology providers may process information outside the UK.',
          'Where personal information is transferred internationally, we will use appropriate safeguards required by UK data protection law, such as applicable adequacy regulations or approved contractual safeguards.',
        ],
      },
      {
        h: '8. How long we keep information',
        body: [
          'We retain information only for as long as reasonably necessary for the purpose for which it was collected and to meet legal, financial, safeguarding and administrative requirements.',
          'Enquiries that do not result in enrolment will normally be deleted or anonymised when they are no longer reasonably required.',
          'Student, contractual, accounting or safeguarding records may need to be retained for longer periods.',
          'Retention periods are reviewed periodically and information that is no longer required is securely deleted or anonymised.',
        ],
      },
      {
        h: '9. Marketing',
        body: [
          'We may occasionally contact existing customers about relevant Science Sorted services where permitted by law.',
          'Where marketing consent is required, we will obtain it.',
          'You can unsubscribe from marketing communications at any time. Service messages relating to an existing tuition arrangement are not marketing and may still need to be sent.',
        ],
      },
      {
        h: '10. Your rights',
        body: [
          'Depending on the circumstances, you may have the right to request access to your information, ask for incorrect information to be corrected, request deletion, restrict certain processing, object to certain uses of your information, request transfer of certain information or withdraw consent where processing is based on consent.',
          'Requests can be sent to: info@sciencesorted.co.uk',
        ],
      },
      {
        h: '11. Security',
        body: [
          'We take reasonable technical and organisational measures to protect personal information against unauthorised access, alteration, disclosure, loss or misuse.',
          'Access to student information is restricted to individuals who reasonably need it for their role.',
        ],
      },
      {
        h: '12. Complaints',
        body: [
          'If you have a concern about how Science Sorted uses personal information, please contact us first at info@sciencesorted.co.uk.',
          'You also have the right to raise a complaint with the UK Information Commissioner\u2019s Office.',
        ],
      },
      {
        h: '13. Changes to this policy',
        body:
          'We may update this Privacy Policy when our services, technology or legal obligations change. The current version will always be published on our website.',
      },
    ],
  },

  terms: {
    title: 'Terms & Conditions',
    lastUpdated: 'August 2026',
    intro:
      'These Terms & Conditions apply to tuition and associated services supplied by Science Sorted Ltd.',
    sections: [
      {
        h: '1. About these terms',
        body: [
          'These Terms & Conditions apply to tuition and associated services supplied by Science Sorted Ltd.',
          'When a student is under 18, the contract for tuition will normally be between Science Sorted and the student\u2019s parent or legal guardian.',
          'By booking or paying for tuition, you agree to these terms together with our Privacy Policy and Cancellation & Refund Policy.',
        ],
      },
      {
        h: '2. Our tuition services',
        body: [
          'Science Sorted provides live online science tuition, currently including GCSE Science and A Level Biology courses.',
          'Courses, year groups, exam boards, lesson frequency, pricing and availability are displayed on our website or communicated during enrolment.',
          'We aim to provide tuition with reasonable care and skill and in accordance with the course information supplied to you. UK consumers have statutory rights relating to services, including the requirement for services to be performed with reasonable care and skill. These rights cannot simply be excluded through contractual terms.',
        ],
      },
      {
        h: '3. Free trial lessons',
        body: [
          'Where advertised, new students may attend an eligible free trial lesson.',
          'A free trial creates no obligation to continue with paid tuition.',
          'Availability of trial lessons is subject to class capacity and the suitability of the relevant course.',
        ],
      },
      {
        h: '4. Fees',
        body: [
          'Current prices will be displayed on our website or confirmed before enrolment.',
          'At the time of publication:',
          [
            'GCSE tuition is \u00a315 per one-hour subject session when taking one or two sciences, or \u00a313 per subject session when enrolled in all three GCSE sciences.',
            'A Level tuition is \u00a319 per one-hour session.',
          ],
          'Science Sorted may change prices in the future. Existing customers will receive reasonable notice of changes that affect them.',
        ],
      },
      {
        h: '5. Payment',
        body: [
          'Invoices and payment arrangements will be communicated during enrolment.',
          'Payments must be made by the stated due date.',
          'Where payments remain overdue, Science Sorted may suspend tuition after reasonable notice until outstanding amounts are resolved.',
        ],
      },
      {
        h: '6. Attendance',
        body: [
          'Students are expected to attend lessons punctually and participate appropriately.',
          'Parents or students should notify Science Sorted as soon as reasonably possible where a student cannot attend.',
          'Missed sessions are dealt with in accordance with our Cancellation Policy.',
        ],
      },
      {
        h: '7. Online lessons and technology',
        body: [
          'Students are responsible for having a suitable internet connection and compatible device.',
          'Science Sorted is not responsible for interruption caused by the student\u2019s own device, internet connection or home technology.',
          'Where a technical failure on Science Sorted\u2019s side materially prevents a lesson from being delivered, we will take reasonable steps to reschedule, credit or otherwise remedy the affected session.',
        ],
      },
      {
        h: '8. Behaviour',
        body: [
          'Students must behave respectfully towards teachers and other students.',
          'Bullying, harassment, discriminatory behaviour, inappropriate content, deliberate disruption or misuse of lesson links may result in removal from a lesson and, where appropriate, termination of tuition.',
          'Safeguarding concerns will be dealt with under our Safeguarding Policy.',
        ],
      },
      {
        h: '9. Communication',
        body: [
          'Communication with students under 18 should take place through appropriate Science Sorted channels.',
          'Parents and guardians should keep Science Sorted informed of relevant changes to contact details or circumstances affecting tuition.',
        ],
      },
      {
        h: '10. Learning resources and intellectual property',
        body: [
          'Teaching slides, flashcards, worksheets, recordings where provided, assessments and other Science Sorted resources are supplied for the enrolled student\u2019s personal educational use.',
          'They may not be sold, publicly shared, uploaded to other websites, distributed to third parties or commercially reproduced without written permission.',
          'Copyright and other intellectual-property rights remain with Science Sorted or the relevant third-party rights holder.',
        ],
      },
      {
        h: '11. Exam questions and third-party material',
        body: [
          'Some learning resources may refer to examination specifications or legitimately use third-party educational material.',
          'Science Sorted is an independent tuition provider and is not affiliated with or endorsed by AQA or OCR.',
        ],
      },
      {
        h: '12. Results',
        body: [
          'We work to improve students\u2019 understanding, examination technique and confidence. However, examination outcomes depend on many factors outside Science Sorted\u2019s control.',
          'No particular grade, mark, university offer or examination result is guaranteed.',
        ],
      },
      {
        h: '13. Changes to lessons',
        body: [
          'We may occasionally need to change a teacher, lesson time, lesson format or other operational detail because of illness, emergencies or other reasonable circumstances.',
          'Where a significant change affects a student\u2019s tuition, we will provide reasonable notice wherever possible and seek an appropriate alternative.',
        ],
      },
      {
        h: '14. Ending tuition',
        body: [
          'Parents may end tuition in accordance with our Cancellation & Refund Policy.',
          'Science Sorted may end or suspend tuition where there is serious or repeated non-payment, inappropriate behaviour, safeguarding concerns, misuse of our systems or a serious breach of these terms.',
        ],
      },
      {
        h: '15. Liability',
        body: [
          'Nothing in these terms excludes or limits liability where it would be unlawful to do so or affects your statutory consumer rights.',
          'Science Sorted will not be responsible for losses that were not reasonably foreseeable when the contract was entered into or for losses arising from circumstances outside our reasonable control, subject always to applicable consumer law.',
        ],
      },
      {
        h: '16. Complaints',
        body: [
          'If you are dissatisfied with any aspect of Science Sorted, please contact: info@sciencesorted.co.uk',
          'We will aim to investigate concerns fairly and respond within a reasonable time.',
        ],
      },
      {
        h: '17. Governing law',
        body: [
          'These terms are governed by the laws of England and Wales.',
          'Consumers retain any mandatory rights they have under applicable law.',
        ],
      },
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    lastUpdated: 'August 2026',
    intro:
      'This policy explains how Science Sorted uses cookies and similar technologies on this website.',
    sections: [
      {
        h: 'What are cookies?',
        body: [
          'Cookies are small files or similar technologies that websites can store or access on a user\u2019s device.',
          'They can be used for essential website functions, remembering preferences, measuring website use and, where enabled, advertising or marketing.',
        ],
      },
      {
        h: 'How Science Sorted uses cookies',
        body: [
          'Science Sorted may use strictly necessary cookies to provide core website functions, maintain security, remember privacy preferences and operate website forms.',
          'Where analytics, advertising or other non-essential technologies are used, we will ask for consent before activating them.',
          'Under UK PECR rules, website operators generally need to explain cookies and obtain active consent before using cookies that are not strictly necessary for a service requested by the user. Simply continuing to browse is not sufficient consent.',
        ],
      },
      {
        h: 'Strictly necessary cookies',
        body: [
          'These technologies are required for functions such as website security, forms, session management and remembering privacy choices.',
          'They cannot normally be disabled through our cookie preference tool because the website may not function correctly without them.',
        ],
      },
      {
        h: 'Analytics cookies',
        body: [
          'If enabled, analytics technologies help us understand how visitors use our website, such as which pages are visited and how visitors navigate the site.',
          'We will not activate non-essential analytics cookies until the required consent has been obtained.',
        ],
      },
      {
        h: 'Marketing cookies',
        body: [
          'If Science Sorted uses advertising technologies such as social-media advertising pixels, those technologies will only be enabled where the necessary consent has been provided.',
          'Advertising and tracking technologies do not fall within the strictly necessary exemption simply because they are commercially useful to the website owner.',
        ],
      },
      {
        h: 'Third-party services',
        body: [
          'Some website features may connect to third-party providers, for example video, analytics, booking or social-media services.',
          'Those providers may use their own technologies subject to their policies and the permissions you select.',
        ],
      },
      {
        h: 'Managing your preferences',
        body: [
          'You can accept or reject non-essential cookies through our cookie banner or preference centre.',
          'You can change your preferences later using the Cookie Settings link on our website.',
          'Your browser also allows you to block or delete cookies, although blocking essential cookies may affect website functionality.',
        ],
      },
      {
        h: 'Current cookie list',
        body:
          'A live list of the cookies actually in use on this website will be published here once the site\u2019s final analytics and marketing configuration is confirmed. The table will show: Cookie / provider \u00b7 Purpose \u00b7 Category \u00b7 Duration.',
      },
    ],
  },

  safeguarding: {
    title: 'Safeguarding Policy',
    lastUpdated: 'August 2026',
    intro:
      'The safety and wellbeing of every child we teach is our highest priority. This statement summarises how we protect the young people in our care.',
    sections: [
      {
        h: '1. Our commitment',
        body: [
          'Science Sorted is committed to safeguarding and promoting the welfare of children and young people who use our services.',
          'For safeguarding purposes, a child is a person under the age of 18.',
          'Our tuition is delivered online, so safeguarding includes both traditional child-protection responsibilities and online safety.',
          'Although Keeping Children Safe in Education is statutory guidance specifically for schools and colleges, Science Sorted uses its principles and relevant remote-learning guidance as important safeguarding benchmarks for our own practice. The current 2025 version remains in force until 31 August 2026, with KCSIE 2026 taking effect from 1 September 2026.',
        ],
      },
      {
        h: '2. Safeguarding lead',
        body: [
          'Designated Safeguarding Lead: Qasid Ahmed',
          'Safeguarding email: info@sciencesorted.co.uk',
        ],
      },
      {
        h: '3. Our safeguarding principles',
        body: [
          'The welfare of the child is our primary consideration.',
          'Children should be treated with dignity and respect and should be able to participate in tuition without bullying, harassment, exploitation, discrimination or inappropriate behaviour.',
          'Safeguarding concerns will be taken seriously and acted upon promptly.',
          'Information relating to safeguarding will only be shared where appropriate and necessary to protect a child or meet a legal obligation.',
        ],
      },
      {
        h: '4. Tutor conduct',
        body: [
          'Tutors must maintain appropriate professional boundaries with students.',
          'Communication should take place through approved Science Sorted channels rather than private or personal accounts wherever possible.',
          'Tutors must not engage in inappropriate, sexualised, discriminatory or exploitative communication with students.',
          'Tutors should not unnecessarily share personal information with students.',
          'Online lessons should be delivered from an appropriate environment, and tutors should use professional or neutral backgrounds.',
          'DfE remote-learning guidance similarly recommends professional communication channels, secure login credentials and appropriate backgrounds for video teaching.',
        ],
      },
      {
        h: '5. Online lessons',
        body: [
          'Lesson links must not be shared with unauthorised individuals.',
          'Students should use appropriate display names and behave respectfully during lessons.',
          'Students are not required to disclose unnecessary personal information to other participants.',
          'Science Sorted may apply controls such as muting participants, restricting screen sharing or limiting camera use where appropriate for safety and lesson management.',
        ],
      },
      {
        h: '6. Parents and guardians',
        body: [
          'Parents or guardians are responsible for providing accurate contact information and informing Science Sorted of relevant safeguarding, accessibility or welfare issues that may materially affect tuition.',
          'Parents should know when younger students are participating in online lessons and should ensure the student has an appropriate environment from which to participate.',
        ],
      },
      {
        h: '7. Concerns and disclosures',
        body: [
          'A tutor who becomes concerned that a child may be at risk of abuse, neglect, exploitation or other serious harm must not investigate the matter themselves.',
          'They should listen appropriately, avoid promising absolute confidentiality, make an accurate factual record and report the concern promptly to the Designated Safeguarding Lead.',
          'Where there is an immediate risk of serious harm, appropriate emergency or statutory services will be contacted.',
        ],
      },
      {
        h: '8. Allegations concerning tutors',
        body: [
          'Any allegation or safeguarding concern involving a tutor, member of staff or other adult connected with Science Sorted will be taken seriously.',
          'The concern must be referred immediately to the Designated Safeguarding Lead or, where the allegation concerns that person, another designated senior person.',
          'Science Sorted will cooperate with appropriate statutory safeguarding authorities where required.',
        ],
      },
      {
        h: '9. Recruitment and suitability',
        body: [
          'Science Sorted aims to use appropriate recruitment and vetting procedures for individuals working with children.',
          'This includes identity, qualification, reference and safeguarding checks as appropriate to the role, together with DBS checks where legally appropriate or required.',
        ],
      },
      {
        h: '10. Recording lessons',
        body: [
          'If Science Sorted records online lessons, parents and students must be clearly informed about when and why recording takes place, how recordings are stored, who may access them and how long they are retained.',
          'Recordings must not be independently recorded, copied or distributed by students without permission.',
        ],
      },
      {
        h: '11. Online safety',
        body: [
          'Students should not share passwords, lesson links or personal information with other users.',
          'Science Sorted may remove participants or restrict access where online behaviour creates a safeguarding or security concern.',
          'Clear routes should exist for students and parents to report concerns. DfE guidance emphasises the importance of communicating clear reporting routes in remote education.',
        ],
      },
      {
        h: '12. Confidentiality and information sharing',
        body: [
          'Safeguarding information will be handled sensitively.',
          'Confidentiality cannot be guaranteed where withholding information could place a child or another person at risk.',
          'Information may be shared with parents, safeguarding authorities, police or other appropriate bodies where necessary and lawful.',
        ],
      },
      {
        h: '13. Review',
        body:
          'This policy will be reviewed regularly and whenever there is a material change in safeguarding guidance, legislation or Science Sorted\u2019s services.',
      },
    ],
  },

  refunds: {
    title: 'Cancellation Policy',
    lastUpdated: 'August 2026',
    intro:
      'This policy explains how you can cancel a booking with Science Sorted.',
    sections: [
      {
        h: '1. Free trial lessons',
        body: [
          'Eligible new students may attend one free trial lesson.',
          'There is no charge for the trial and no obligation to continue afterwards.',
        ],
      },
      {
        h: '2. Cancelling ongoing tuition',
        body: [
          'Parents or guardians may cancel ongoing tuition by providing at least 3 days\u2019 written notice to: info@sciencesorted.co.uk or by contacting the teacher directly via the WhatsApp number.',
          'Tuition scheduled during the notice period remains payable unless otherwise agreed.',
          'After the notice period expires, no further tuition fees will be charged.',
        ],
      },
      {
        h: '3. Individual missed lessons',
        body: [
          'Group lessons operate according to a scheduled programme.',
          'Where a student chooses not to attend or misses a group lesson, the lesson is normally still chargeable because the teacher and place in the class have been reserved.',
          'Where appropriate, Science Sorted may provide the relevant materials or catch-up resources.',
          'Credits or alternative arrangements may occasionally be offered at Science Sorted\u2019s discretion in exceptional circumstances, but are not guaranteed.',
        ],
      },
      {
        h: '4. Lessons cancelled by Science Sorted',
        body: [
          'If Science Sorted cancels a lesson and cannot provide a reasonable alternative, the affected lesson will be rescheduled, credited or refunded.',
          'Students will not be charged for tuition that Science Sorted does not provide.',
        ],
      },
      {
        h: '5. Technical problems',
        body: [
          'Where a student\u2019s internet connection, device or other local technical issue prevents attendance, the session will normally be treated as a missed student lesson.',
          'Where a significant technical problem on Science Sorted\u2019s side prevents the lesson from being delivered, we will take reasonable steps to reschedule or credit the session.',
        ],
      },
      {
        h: '6. Ending tuition because of conduct',
        body: [
          'Science Sorted reserves the right to suspend or terminate tuition where there is serious or repeated inappropriate behaviour, non-payment, misuse of lesson systems or safeguarding concerns.',
          'Where Science Sorted ends tuition, any refund due for genuinely prepaid future tuition will be considered in accordance with the circumstances and applicable consumer law.',
        ],
      },
    ],
  },
};

function SectionBody({ body }) {
  if (Array.isArray(body)) {
    return (
      <>
        {body.map((item, i) => {
          if (Array.isArray(item)) {
            return (
              <ul key={i} className="mt-3 mb-3 ml-5 list-disc space-y-1 text-brand-ink/80">
                {item.map((li, j) => (
                  <li key={j} className="leading-relaxed">{li}</li>
                ))}
              </ul>
            );
          }
          return (
            <p key={i} className="mt-2 text-brand-ink/80 leading-relaxed">{item}</p>
          );
        })}
      </>
    );
  }
  return <p className="mt-2 text-brand-ink/80 leading-relaxed">{body}</p>;
}

export default function PolicyPage({ slug }) {
  const p = policies[slug];
  if (!p) {
    return (
      <main className="container py-16 md:py-20 text-center">
        <h1 className="font-display text-3xl md:text-4xl text-brand-ink">Page not found</h1>
        <Link to="/" className="mt-4 inline-block text-brand-teal underline">Back to home</Link>
      </main>
    );
  }

  return (
    <main>
      <section className="pt-10 md:pt-14 pb-8 bg-gradient-to-b from-brand-butter/40 to-brand-cream">
        <div className="container max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-brand-ink/70 hover:text-brand-ink focus-visible:outline-none focus-visible:underline"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back home
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border border-brand-ink/10 px-3.5 py-1.5 text-sm text-brand-ink">
            <FileText className="w-4 h-4 text-brand-teal" aria-hidden="true" /> Policy
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink">
            {p.title}
          </h1>
          <p className="mt-3 text-brand-ink/70 text-base md:text-lg">{p.intro}</p>
          <p className="mt-2 text-xs text-brand-ink/50">Last updated: {p.lastUpdated}</p>
        </div>
      </section>

      <section className="py-10 md:py-14 container max-w-3xl">
        {p.sections.map((s, i) => (
          <div key={i} className="mb-6 md:mb-8">
            <h2 className="font-display text-xl md:text-2xl text-brand-ink">{s.h}</h2>
            <SectionBody body={s.body} />
          </div>
        ))}
      </section>
    </main>
  );
}
